import {
  configureStore,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { API_URL } from "./utils";
import { CANCELLED_STATUS, CREATED_STATUS } from "./constants/sessions";
import { getFromLocalStorage } from "./utils/localStorageUtil";
import { COMPLETED_STATUS } from "./utils/sessionHelper";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "./utils/firebaseConfig";

interface SessionState {
  sessions: {
    data: any;
    loading: boolean;
    error: any;
    activeSession: any;
  };
  requests: {
    data: any;
    loading: boolean;
    error: any;
    activeSession: any;
  };
  unreadSessionRequests: number;
  updatingSession: {
    loading: boolean;
  };
  appointmentTypes: {
    data: any[];
    loading: boolean;
    error: any;
  };
  cognitoUsers: {
    data: any[];
    loading: boolean;
    error: any;
  };
  allFirms: {
    data: any[];
    loading: boolean;
    error: any;
  };
}
const initialState: SessionState = {
  sessions: {
    data: [],
    loading: false,
    error: null,
    activeSession: null,
  },
  requests: {
    data: [],
    loading: false,
    error: null,
    activeSession: null,
  },
  cognitoUsers: {
    data: [],
    loading: false,
    error: null,
  },
  allFirms: {
    data: [],
    loading: false,
    error: null,
  },
  unreadSessionRequests: 0,
  updatingSession: {
    loading: false,
  },
  appointmentTypes: {
    data: [],
    loading: false,
    error: null,
  },
};

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    updateSessionRequestInStore: (state, action: PayloadAction<{ sessionId: string; updates: any }>) => {
      const { sessionId, updates } = action.payload;
      const sessionIndex = state.requests.data.findIndex((session: any) => session.id === sessionId);

      if (sessionIndex !== -1) {
        state.requests.data[sessionIndex] = {
          ...state.requests.data[sessionIndex],
          ...updates
        };
      }
    },
    setActiveSessionRequest: (state, action: PayloadAction<any>) => {
      state.requests.activeSession = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSessions.fulfilled, (state, action) => {
      state.sessions.data = action.payload?.isNextPageQuery
        ? [...state.sessions.data, ...action.payload?.data?.reverse()]
        : action.payload?.data?.reverse();
      state.sessions.loading = false;
    });

    builder.addCase(getSessions.pending, (state) => {
      state.sessions.loading = true;
      state.sessions.data = [];
    });

    builder.addCase(getSessions.rejected, (state) => {
      state.sessions.loading = false;
      state.sessions.data = [];
    });
  

    builder.addCase(sessionBatch.fulfilled, (state, action) => {
      // const updatedSessions = action.payload.data;
      // state.sessions.data = updatedSessions;
    });

    builder.addCase(sessionBatch.pending, (state) => {
      // state.updatingSession.loading = true;
    });

    builder.addCase(sessionBatch.rejected, (state) => {
      // state.updatingSession.loading = false;
    });
    builder.addCase(updateSessionDetail.fulfilled, (state, action) => {
      state.updatingSession.loading = false;
    });

    builder.addCase(updateSessionDetail.pending, (state) => {
      state.updatingSession.loading = true;
    });

    builder.addCase(updateSessionDetail.rejected, (state) => {
      state.updatingSession.loading = false;
    });
    builder.addCase(getAppointmentTypes.pending, (state) => {
      state.appointmentTypes.loading = true;
      state.appointmentTypes.error = null;
    });

    builder.addCase(getAppointmentTypes.fulfilled, (state, action) => {
      state.appointmentTypes.loading = false;
      state.appointmentTypes.data = action.payload?.map((type: any) => {
        return {
          ...type,
          value: type.type,
          label: type.type
        }
      });
    });

    builder.addCase(getAppointmentTypes.rejected, (state, action) => {
      state.appointmentTypes.loading = false;
      state.appointmentTypes.error = action.payload;
    });
    builder.addCase(fetchCognitoUsers.pending, (state) => {
      state.cognitoUsers.loading = true;
      state.cognitoUsers.error = null;
    });

    builder.addCase(fetchCognitoUsers.fulfilled, (state, action) => {
      state.cognitoUsers.loading = false;
      state.cognitoUsers.data = action.payload?.Users || [];
    });

    builder.addCase(fetchCognitoUsers.rejected, (state, action) => {
      state.cognitoUsers.loading = false;
      state.cognitoUsers.error = action.payload;
    });
    builder.addCase(fetchFirmsFromFirebase.pending, (state) => {
      state.allFirms.loading = true;
      state.allFirms.error = null;
    });

    builder.addCase(fetchFirmsFromFirebase.fulfilled, (state, action) => {
      state.allFirms.loading = false;
      state.allFirms.data = action.payload;
    });

    builder.addCase(fetchFirmsFromFirebase.rejected, (state, action) => {
      state.allFirms.loading = false;
      state.allFirms.error = action.payload;
    });
    builder.addCase(getNotarySessions.fulfilled, (state, action) => {
      let validBefore = 90 * 60 * 1000;
      let filteredNotarySessionsForCount = action.payload?.items?.filter(
        (item: any) => {
          return (
            item?.isConfirmed === "1" &&
            new Date(item.requestedDatetime).getTime() - new Date().getTime() <
            validBefore
          );
        }
      );
      state.unreadSessionRequests = filteredNotarySessionsForCount?.length;
      
      let filteredNotarySessions = action.payload?.items?.filter(
        (item: any) => item?.isConfirmed === "1"
      );

      const sorted = filteredNotarySessions?.sort((a: any, b: any) => {
        const dateA = new Date(a.requestedDatetime).getTime();
        const dateB = new Date(b.requestedDatetime).getTime();
        return dateA - dateB;
      });

      const next = sorted || [];
      const prev = state.requests.data || [];
      const isSameList =
        prev.length === next.length &&
        prev.every((item: any, index: number) => item?.id === next[index]?.id);

      if (!isSameList) {
        state.requests.data = next;
      }
    });
  },
});

export const getSessions = createAsyncThunk<
  any,
  { status: string; search?: string; cursor?: string },
  { rejectValue: any }
>(
  "session/getSessions",
  async ({ status, search, cursor }, { rejectWithValue }) => {
    try {
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const encodedCursor = cursor ? encodeURIComponent(cursor) : "";
      const encodedSearch = search ? encodeURIComponent(search) : "";
      let routeUri =
        status === "completed" && encodedSearch
          ? encodedCursor
            ? `/sessions?status=completed&search=${encodedSearch}&cursor=${encodedCursor}`
            : `/sessions?status=completed&search=${encodedSearch}`
          : status === "completed"
            ? encodedCursor
              ? `/sessions?status=completed&cursor=${encodedCursor}`
              : `/sessions?status=completed`
            : encodedSearch
              ? encodedCursor
                ? `/sessions?status=active&search=${encodedSearch}&cursor=${encodedCursor}`
                : `/sessions?status=active&search=${encodedSearch}`
              : encodedCursor
                ? `/sessions?status=active&cursor=${encodedCursor}`
                : `/sessions?status=active`;

      const response = await fetch(API_URL + routeUri, {
        headers: {
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }
      return { data: data, isNextPageQuery: cursor ? true : false };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getSessionsByAutoSearch = createAsyncThunk<
  any,
  { type: string; search: string; status: string; cursor?: string },
  { rejectValue: any }
>(
  "session/getSessionsByAutoSearch",
  async ({ type, search, status, cursor }, { rejectWithValue }) => {
    try {
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");

      const encodedType = encodeURIComponent(type);
      const encodedSearch = encodeURIComponent(search);
      const encodedStatus = encodeURIComponent(status);
      const encodedCursor = cursor ? encodeURIComponent(cursor) : "";

      let routeUri = `/sessions/autocomplete-search?type=${encodedType}&search=${encodedSearch}&status=${encodedStatus}`;
      if (cursor) {
        routeUri += `&cursor=${encodedCursor}`;
      }

      const response = await fetch(API_URL + routeUri, {
        headers: {
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return {
        data: data,
        isNextPageQuery: !!cursor,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteDocumentSessionRequest = createAsyncThunk<
  any,
  { sessionId: string; documentId: string },
  { rejectValue: any }
>(
  "session/deleteDocumentSessionRequest",
  async ({ sessionId, documentId }, { rejectWithValue }) => {
    try {
      const firmId = getFromLocalStorage("firmId");
      const officialId = getFromLocalStorage("officialId");

      const response = await fetch(
        `${API_URL}/document-groups/${sessionId}/documents/${documentId}`,
        {
          method: "DELETE",
          headers: {
            "X-Firm-Id": firmId,
            "X-Official-Id": officialId,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      return { sessionId, documentId };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

let notarySessionsAbortController: AbortController | null = null;
let isFetchingNotarySessions = false;

export const getNotarySessions = createAsyncThunk<
  any,
  {},
  { rejectValue: any }
>("session/getNotarySessions", async (_, { rejectWithValue }) => {
  try {
    if (isFetchingNotarySessions) {
      console.log("⚠️ Fetch in progress — skipping duplicate call.");
      return;
    }

    isFetchingNotarySessions = true;

    if (notarySessionsAbortController) {
      notarySessionsAbortController.abort();
    }

    notarySessionsAbortController = new AbortController();

    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");

    const response = await fetch(
      API_URL + "/document-groups?type=notaryRequest",
      {
        signal: notarySessionsAbortController.signal,
        headers: {
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
      }
    );


    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return data;
  } catch (error: any) {
    if (error.name === "AbortError") {
      console.warn("🚫 Notary sessions fetch aborted");
      return rejectWithValue({ message: "Request aborted" });
    }
    return rejectWithValue(error);
  } finally {
    isFetchingNotarySessions = false;
    notarySessionsAbortController = null;
  }
});

export const updateRequestDetail = createAsyncThunk<
  any,
  { requestId: number; details: any },
  { rejectValue: any }
>(
  "session/updateRequestDetail",
  async ({ details, requestId }, { rejectWithValue }) => {
    try {
      let body = { ...details };
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(API_URL + `/document-groups/${requestId}`, {
        method: "PUT",
        headers: {
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createSession = createAsyncThunk<
  any,
  {
    documentsCount: number;
    emailCopies: any[];
    name: string;
    officialPrivateNotes: string | null;
    scheduledAt: number;
    message: string;
    typeOfNotarization: number;
    user: any;
    isPersonallyKnown: boolean;
    officialNotes: string;
    multipleSigner: boolean;
    official: string;
    userTimezone?: string;
    userPhone?: string;
    payerEmail?: string;
    appointmentType?: string;
    appointmentId?: number;
    paymentSettings?: any;
  },
  {
    rejectValue: any;
  }
>(
  "session/createSession",
  async (
    {
      documentsCount,
      emailCopies,
      name,
      officialPrivateNotes,
      scheduledAt,
      typeOfNotarization,
      user,
      message,
      isPersonallyKnown,
      multipleSigner,
      official,
      userTimezone,
      userPhone,
      officialNotes,
      payerEmail,
      appointmentType,
      appointmentId,
      paymentSettings,
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      let body = {
        documentsCount,
        emailCopies,
        name,
        officialPrivateNotes,
        message,
        scheduledAt,
        typeOfNotarization,
        user,
        isPersonallyKnown,
        multipleSigner,
        official,
        userTimezone,
        userPhone,
        officialNotes,
        payerEmail,
        appointmentType,
        appointmentId,
        paymentSettings
      };
      if (official) {
        body = { ...body, official };
      }
      if (userTimezone) {
        body = { ...body, userTimezone };
      }
      if (userPhone) {
        body = { ...body, userPhone };
      }
      if (official) {
        body = { ...body, official };
      }
      if (officialNotes) {
        body = { ...body, officialNotes };
      }
      if (payerEmail) {
        body = { ...body, payerEmail };
      }
      if (message) {
        body = { ...body, message };
      }
      if (appointmentType) {
        body = { ...body, appointmentType };
      }
      if (appointmentId) {
        body = { ...body, appointmentId };
      }
      if (paymentSettings) {
        body = { ...body, paymentSettings };
      }
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(API_URL + "/sessions", {
        headers: {
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
        method: "POST",
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const markSessionAsViewed = createAsyncThunk<
  any,
  {
    sessionId: string;
  },
  {
    rejectValue: any;
  }
>(
  "session/markSessionAsViewed",
  async ({ sessionId }, { rejectWithValue, dispatch }) => {
    try {
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(
        API_URL + `/sessions/${sessionId}/mark-viewed`,
        {
          headers: {
            "X-Firm-Id": firmId,
            "X-Official-Id": officialId,
          },
          method: "POST",
        }
      );
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const verifyOfficialSession = createAsyncThunk<
  any,
  { uid: string; roles?: any },
  { rejectValue: string }
>('session/verifyOfficialSession', async ({ uid, roles = 'official' }, { rejectWithValue }) => {
  try {
    const res = await fetch(
      `${API_URL}/sessions/verifyOfficial?uid=${uid}&roles=${roles}`,
      {
        method: 'GET',
      }
    );

    const data = await res.json();
    if (!res.ok || !data.token) {
      return rejectWithValue(data.error || 'Failed to verify official session');
    }
    const user = await signInWithCustomToken(auth, data.token);
    const result = await auth?.currentUser?.getIdTokenResult();
    return { success: true, user };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Something went wrong');
  }
});

export const sessionBatch = createAsyncThunk<
  any,
  {
    sessionIds: any[];
  },
  {
    rejectValue: any;
  }
>(
  "session/sessionBatch",
  async ({ sessionIds }, { rejectWithValue, dispatch }) => {
    try {
      const body = {
        sessionIds,
      };
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(API_URL + "/sessions/batch", {
        headers: {
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
        method: "POST",
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendDocumentRequest = createAsyncThunk<
  any,
  {
    email: string;
    instructions?: string;
    officialEmail?: string;
    appointmentId?: any;
  },
  {
    rejectValue: any;
  }
>(
  "session/sendDocumentRequest",
  async (
    { email, instructions, officialEmail, appointmentId },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const body = {
        message: instructions,
        officialEmail,
        userEmail: email,
      };
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(
        API_URL + "/upload-document-request/" + appointmentId,
        {
          headers: {
            "X-Firm-Id": firmId,
            "X-Official-Id": officialId,
          },
          method: "POST",
          body: JSON.stringify(body),
        }
      );
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDocument = createAsyncThunk<
  any,
  { sessionId: string },
  {
    rejectValue: any;
  }
>(
  "session/getDocument",
  async ({ sessionId }, { rejectWithValue, dispatch }) => {
    try {
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(
        API_URL + `/documents?session-id=${sessionId}`,
        {
          headers: {
            "X-Firm-Id": firmId,
            "X-Official-Id": officialId,
          },
        }
      );
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteDocument = createAsyncThunk<
  any,
  { documentId: string },
  {
    rejectValue: any;
  }
>("session/deleteDocument", async ({ documentId }, { rejectWithValue }) => {
  try {
    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");
    const response = await fetch(`${API_URL}/documents/${documentId}/session`, {
      method: "DELETE",
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
        "Content-Type": "application/json",
      },
    });
    // const data = await response.json();

    // if (!response.ok) {
    //   return rejectWithValue(data);
    // }

    // return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const fetchUsers = createAsyncThunk<
  any,
  {},
  {
    rejectValue: any;
  }
>("session/fetchUsers", async ({ }, { rejectWithValue }) => {
  try {
    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");
    const response = await fetch(`${API_URL}/users`, {
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const fetchFirms = createAsyncThunk<
  any,
  {},
  {
    rejectValue: any;
  }
>("session/fetchFirms", async ({ }, { rejectWithValue }) => {
  try {
    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");
    const response = await fetch(`${API_URL}/firms`, {
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const fetchFirmsFromFirebase = createAsyncThunk<
  any,
  void,
  { rejectValue: any }
>("session/fetchFirmsFromFirebase", async (_, { rejectWithValue }) => {
  try {
    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");
    const response = await fetch(`${API_URL}/firmsFromFirebase`, {
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
      },
    });

    const data = await response.json();
    if (!response.ok) {
      return rejectWithValue(data);
    }

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateDocument = createAsyncThunk<
  any,
  { documentId: string; name: string },
  {
    rejectValue: any;
  }
>("session/updateDocument", async ({ documentId, name }, { rejectWithValue }) => {
  try {
    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");

    const response = await fetch(`${API_URL}/documents/${documentId}/session`, {
      method: "PUT",
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name }),
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});


export const deleteRequest = createAsyncThunk<
  any,
  { requestId: string },
  {
    rejectValue: any;
  }
>("session/deleteRequest", async ({ requestId }, { rejectWithValue }) => {
  try {
    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");
    const response = await fetch(`${API_URL}/document-groups/${requestId}`, {
      method: "DELETE",
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
        "Content-Type": "application/json",
      },
    });
    // const data = await response.json();

    // if (!response.ok) {
    //   return rejectWithValue(data);
    // }

    // return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const createSessionRequest = createAsyncThunk<
  any,
  { payload: any },
  {
    rejectValue: any;
  }
>("session/createSessionRequest", async ({ payload }, { rejectWithValue }) => {
  try {
    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");
    const response = await fetch(`${API_URL}/document-groups`, {
      method: "POST",
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    // const data = await response.json();

    // if (!response.ok) {
    //   return rejectWithValue(data);
    // }

    // return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const deleteSession = createAsyncThunk<
  any,
  { sessionId: string },
  {
    rejectValue: any;
  }
>("session/deleteSession", async ({ sessionId }, { rejectWithValue }) => {
  try {
    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");
    const response = await fetch(`${API_URL}/sessions/${sessionId}`, {
      method: "DELETE",
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
        "Content-Type": "application/json",
      },
    });
    // const data = await response.json();

    // if (!response.ok) {
    //   return rejectWithValue(data);
    // }

    // return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getRecords = createAsyncThunk<
  any,
  { status: string; sessionId?: string },
  {
    rejectValue: any;
  }
>(
  "session/getRecords",
  async ({ status = "finished", sessionId }, { rejectWithValue, dispatch }) => {
    try {
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      let route = sessionId
        ? `/records?session-id=${sessionId}&status=${status}&with=screenShareUrl`
        : `/records?session-id=&status=${status}&with=screenShareUrl`;
      const response = await fetch(API_URL + route, {
        headers: {
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getOfficials = createAsyncThunk<
  any,
  {},
  {
    rejectValue: any;
  }
>("session/getOfficials", async (_, { rejectWithValue, dispatch }) => {
  try {
    let firmId = getFromLocalStorage("firmId");
    let officialId = getFromLocalStorage("officialId");
    const response = await fetch(API_URL + `/officials`, {
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
      },
    });
    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getAppointmentTypes = createAsyncThunk<
  any,
  {},
  {
    rejectValue: any;
  }
>("session/getAppointmentTypes", async (_, { rejectWithValue, dispatch }) => {
  try {
    let firmId = getFromLocalStorage("firmId");
    let officialId = getFromLocalStorage("officialId");
    const response = await fetch(API_URL + `/appointmentTypes`, {
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
      },
    });
    const data = await response.json();
    if (!response.ok) {
      return rejectWithValue(data);
    }

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateRecord = createAsyncThunk<
  any,
  { recordId: string; updateData: Record<string, any> },
  { rejectValue: any }
>(
  "session/updateRecord",
  async ({ recordId, updateData }, { rejectWithValue }) => {
    try {
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(API_URL + `/records/${recordId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
        body: JSON.stringify(updateData),
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const uploadDoc = createAsyncThunk<
  any,
  { sessionId: string; fileName: string },
  { rejectValue: any }
>("session/uploadDoc", async ({ sessionId, fileName }, { rejectWithValue }) => {
  try {
    let firmId = getFromLocalStorage("firmId");
    let officialId = getFromLocalStorage("officialId");
    const response = await fetch(
      API_URL +
      `/documents/upload-url?session-id=${sessionId}&filename=${fileName}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
          // Authorization:
          //   "Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzUxMiJ9.eyJmaXJtSWQiOiJiODZkNTllNC0zM2IwLTRjZjEtYTUyMC0zNTFiNzRlYTZiMGQiLCJ1c2VySWQiOiI2NThhZDRiNS03NjU3LTQzZGItOThhMC0xYzNlYWI2YWJjZDAifQ.mmET16nl8CmUrxaFgEjPzIj76OSy9WNtaVcvG19nksecn5tSEDt1wWGgQ2oWvCcUyji1ol7DBhm0kQ7Gihm4EQ",
        },
      }
    );
    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const checkSessionStatus = createAsyncThunk<
  any,
  { sessionId: string },
  { rejectValue: any }
>("session/uploadDoc", async ({ sessionId }, { rejectWithValue }) => {
  try {
    let firmId = getFromLocalStorage("firmId");
    let officialId = getFromLocalStorage("officialId");
    const response = await fetch(
      `${API_URL}/sessions/status?session-id=${sessionId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
          // Authorization:
          //   "Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzUxMiJ9.eyJmaXJtSWQiOiJiODZkNTllNC0zM2IwLTRjZjEtYTUyMC0zNTFiNzRlYTZiMGQiLCJ1c2VySWQiOiI2NThhZDRiNS03NjU3LTQzZGItOThhMC0xYzNlYWI2YWJjZDAifQ.mmET16nl8CmUrxaFgEjPzIj76OSy9WNtaVcvG19nksecn5tSEDt1wWGgQ2oWvCcUyji1ol7DBhm0kQ7Gihm4EQ",
        },
      }
    );

    const data = await response.json();
    if (!response.ok) {
      return rejectWithValue(data);
    }
    return data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const getRecordingUrl = createAsyncThunk<
  any,
  { sessionId: string },
  { rejectValue: any }
>("session/getRecordingUrl", async ({ sessionId }, { rejectWithValue }) => {
  try {
    let firmId = getFromLocalStorage("firmId");
    let officialId = getFromLocalStorage("officialId");
    const response = await fetch(`${API_URL}/sessions/${sessionId}/recording`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
        // Authorization:
        //   "Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzUxMiJ9.eyJmaXJtSWQiOiJiODZkNTllNC0zM2IwLTRjZjEtYTUyMC0zNTFiNzRlYTZiMGQiLCJ1c2VySWQiOiI2NThhZDRiNS03NjU3LTQzZGItOThhMC0xYzNlYWI2YWJjZDAifQ.mmET16nl8CmUrxaFgEjPzIj76OSy9WNtaVcvG19nksecn5tSEDt1wWGgQ2oWvCcUyji1ol7DBhm0kQ7Gihm4EQ",
      },
    });

    const data = await response.json();
    if (!response.ok) {
      return rejectWithValue(data);
    }
    return data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const searchSessions = createAsyncThunk<
  any,
  {
    ccEmail?: string;
    payerEmail?: string;
    officialEmail?: string;
    sessionId?: string;
  },
  { rejectValue: any }
>(
  "session/searchSessions",
  async ({ ccEmail, payerEmail, officialEmail, sessionId }, { rejectWithValue }) => {
    try {
      const firmId = getFromLocalStorage("firmId");
      const officialId = getFromLocalStorage("officialId");

      const payload: any = {};
      if (ccEmail) payload.emailCopies = [ccEmail];
      if (payerEmail) payload.payerEmail = payerEmail;
      if (officialEmail) payload["official.email"] = officialEmail;
      if (sessionId) payload.id = sessionId;

      const response = await fetch(`${API_URL}/sessions/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const copyDocument = createAsyncThunk<
  any,
  { sessionId: string; documentId: number; sessionDocumentFilename: string },
  { rejectValue: any }
>(
  "session/copyDocument",
  async (
    { sessionId, documentId, sessionDocumentFilename },
    { rejectWithValue }
  ) => {
    try {
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      let body = {
        sessionId: sessionId,
        sessionDocumentFilename,
      };
      const response = await fetch(
        API_URL + `/documents/${documentId}/copy?sessionId=${sessionId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Firm-Id": firmId,
            "X-Official-Id": officialId,
          },
          body: JSON.stringify(body),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }

      let etag = getFileHash(data, response);

      return { data, etag };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitOfficials = createAsyncThunk<
  any,
  { body: any },
  { rejectValue: any }
>(
  "session/submitOfficials",
  async (
    { body },
    { rejectWithValue }
  ) => {
    try {
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");

      const response = await fetch(
        API_URL + `/officials`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Firm-Id": firmId,
            "X-Official-Id": officialId,
          },
          body: JSON.stringify(body),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }

      let etag = getFileHash(data, response);

      return { data, etag };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUploadUrls = createAsyncThunk<
  any,
  { officialId: string },
  { rejectValue: any }
>(
  "session/getUploadUrls",
  async (
    { officialId },
    { rejectWithValue }
  ) => {
    try {
      const response = await fetch(
        API_URL + `/officials/onboard/get-upload-urls`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ officialId }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return { data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const getFileHash = (data: any, response: any) => {
  let etag;
  if (data && data?.CopyObjectResult && data?.CopyObjectResult?.ETag) {
    etag = data?.CopyObjectResult?.ETag;
  }

  if (!etag) {
    etag = response?.headers && response.headers.get("etag");
  }

  if (!etag) {
    console.error(
      "ETag header was not returned from S3 upload. Check your bucket configuration"
    );

    return null;
  }

  return etag.replace(/(^")|("$)/g, "");
};

export const uploadPDF = createAsyncThunk<
  any,
  { url: string; file: File },
  { rejectValue: any }
>("session/uploadPDF", async ({ url, file }, { rejectWithValue }) => {
  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/pdf",
      },
      body: file,
    });

    const data = await response.text();
    if (!response.ok) {
      return rejectWithValue(data);
    }

    let etag = getFileHash(data, response);

    return { data, etag };
  } catch (error: any) {
    return rejectWithValue(error.message || error);
  }
});

export const getDocumentWithFile = createAsyncThunk<
  any,
  { sessionId: string; fileName: string; name: string; hash: string, documentId: string | null },
  { rejectValue: any }
>(
  "session/getDocumentWithFile",
  async ({ sessionId, fileName, name, hash, documentId }, { rejectWithValue }) => {
    try {
      const body = {
        filename: fileName,
        hash,
        name,
        sessionId,
        documentId
      };
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(
        API_URL + `/documents?filename=${fileName}&sessionId=${sessionId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Firm-Id": firmId,
            "X-Official-Id": officialId,
          },
          body: JSON.stringify(body),
        }
      );
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const generateNotaryRequestUploadUrl = createAsyncThunk<
  any,
  { documentGroupId: string; filename: string; contentType?: string },
  { rejectValue: any }
>("documentGroups/getUploadUrl", async ({ documentGroupId, filename, contentType = "application/pdf" }, { rejectWithValue }) => {
  try {
    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");

    const url = `${API_URL}/document-groups/upload-url?filename=${encodeURIComponent(filename)}&documentGroupId=${documentGroupId}&contentType=${contentType}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
      },
    });

    const data = await response.json();
    if (!response.ok) return rejectWithValue(data);

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const confirmNotaryRequestUpload = createAsyncThunk<
  any,
  {
    documentGroupId: string;
    fileName: string;
    name: string;
  },
  { rejectValue: any }
>(
  "documentGroups/confirmUpload",
  async ({ documentGroupId, fileName, name }, { rejectWithValue }) => {
    try {
      const firmId = getFromLocalStorage("firmId");
      const officialId = getFromLocalStorage("officialId");

      const key = `document-groups-documents/${documentGroupId}/${fileName}`;

      const body = {
        documentGroupId,
        filename: fileName,
        name,
        key,
      };

      const response = await fetch(`${API_URL}/document-groups/confirm-upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) {
        console.error("Upload confirmation failed:", data);
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendNotification = createAsyncThunk<
  any,
  { status: string; notaryAppHost?: string; sessionId: string; note?: string },
  { rejectValue: any }
>(
  "session/sendNotification",
  async (
    {
      sessionId,
      notaryAppHost = process.env.REACT_APP_NOTARY_URL,
      status,
      note = "",
    },
    { rejectWithValue }
  ) => {
    try {
      let body: {
        status: string;
        notaryAppHost?: string | undefined;
        sessionId: string;
        note?: string;
      } = {
        status,
        notaryAppHost,
        sessionId,
      };
      if ((status == CANCELLED_STATUS || status == CREATED_STATUS) && note) {
        body = {
          ...body,
          note: note,
        };
      }
      if (status == COMPLETED_STATUS) {
        body = {
          sessionId,
          status
        };
      }
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(API_URL + `/notifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchCognitoUsers = createAsyncThunk<
  any,
  void,
  { rejectValue: any }
>("session/fetchCognitoUsers", async (_, { rejectWithValue }) => {
  try {
    const firmId = getFromLocalStorage("firmId");
    const officialId = getFromLocalStorage("officialId");
    const response = await fetch(`${API_URL}/aws/getAllUsers`, {
      method: "GET",
      headers: {
        "X-Firm-Id": firmId,
        "X-Official-Id": officialId,
      },
    });

    const data = await response.json();
    if (!response.ok) {
      return rejectWithValue(data);
    }
    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const sendPaymentEmail = createAsyncThunk<
  any,
  { token: string, sessionId: string },
  { rejectValue: any }
>(
  "session/sendPaymentEmail",
  async (
    {
      token,
      sessionId
    },
    { rejectWithValue }
  ) => {
    try {
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(API_URL + `/payment/email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
          "X-Session-Id": sessionId,
        },
        body: JSON.stringify({ token }),
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const searchEmail = createAsyncThunk<
  any,
  { email?: string },
  { rejectValue: any }
>("session/searchEmail", async ({ email }, { rejectWithValue }) => {
  try {
    const body = {
      email,
    };
    const response = await fetch(API_URL + `/officials/search-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(data);
    }

    return data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateSessionDetail = createAsyncThunk<
  any,
  { details: any; sessionId: string },
  { rejectValue: any }
>(
  "session/updateSessionDetail",
  async ({ details, sessionId }, { rejectWithValue }) => {
    try {
      const body = {
        ...details,
      };
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(API_URL + `/sessions/${sessionId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Firm-Id": "da3ba1f6-e983-4b21-9bd5-89331e7d4169",
          "X-Official-Id": officialId,
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendNewUserRegisteredEmail = createAsyncThunk<
  any,
  { name: string; email: string },
  { rejectValue: any }
>(
  "session/sendNewUserRegisteredEmail",
  async ({ name, email }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/notifications/new-user-registered`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email }),
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const generateToken = createAsyncThunk<
  string,
  { days: number; tag: string },
  { rejectValue: any }
>(
  "session/generateToken",
  async ({ days, tag }, { rejectWithValue }) => {
    try {
      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(API_URL + `/token/generate-passkey`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
        body: JSON.stringify({
          days,
          tag,
        }),
      });

      const data = await response.text();

      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getSessionLogs = createAsyncThunk<
  any,
  { sessionId: string },
  { rejectValue: any }
>(
  "session/getSessionLogs",
  async ({ sessionId }, { rejectWithValue }) => {
    try {

      let firmId = getFromLocalStorage("firmId");
      let officialId = getFromLocalStorage("officialId");
      const response = await fetch(API_URL + `/logs/${sessionId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Firm-Id": firmId,
          "X-Official-Id": officialId,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const {updateSessionRequestInStore, setActiveSessionRequest } = sessionSlice.actions;

export const store = configureStore({
  reducer: {
    session: sessionSlice.reducer,
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
